import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import { AnalyticsService, NAVIGATION_EVENTS } from '@qtek/libs/analytics';
import { MenuStore, MetaStore } from '@qtek/libs/store';
import { Environment, MenuNode } from '@qtek/shared/models';

const ROTATE_ANIMATION = trigger('trigger', [
  state(
    '0',
    style({
      transform: 'rotate(0deg)',
    })
  ),
  state(
    '1',
    style({
      transform: 'rotate(180deg)',
    })
  ),
  transition('1 <=> 0', animate('180ms cubic-bezier(0.4, 0, 0.2, 1)')),
]);

@Component({
  selector: 'qt-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ROTATE_ANIMATION],
  host: {
    class: 'sidenav-mini',
  },
  standalone: false,
})
export class SidenavComponent implements OnInit, OnDestroy {
  sidenav = inject(MatSidenav);
  protected readonly menuStore = inject(MenuStore);
  private readonly metaStore = inject(MetaStore);
  private readonly injector = inject(Injector);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly environment = inject<Environment>(APP_ENVIRONMENT);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly media = inject(MediaObserver);
  private readonly changeDetectionRef = inject(ChangeDetectorRef);
  private readonly analytics = inject(AnalyticsService);

  /** Whether mini nav variant should be visible. */
  @Input() mini: boolean;

  /** Whether the sidebar is expanded (from layout); used so chevron stays in sync and doesn’t bounce. */
  @Input() expanded = true;

  /** Emitted when the user clicks the expand/collapse chevron (layout controls opened state). */
  @Output() sidenavToggle = new EventEmitter<void>();

  destroy$ = new Subject<void>();

  @HostListener('window:resize')
  onResize() {
    this.changeDetectionRef.markForCheck();
  }

  constructor() {
    // guiDomain is the actual dependency here (registerLogo needs it); it comes
    // from the same 'ptl' meta bundle as title/mobileTitle.
    this.metaStore.loadGuiDomain();
  }

  ngOnInit() {
    toObservable(this.metaStore.guiDomain, { injector: this.injector })
      .pipe(filter(Boolean), take(1))
      .subscribe((domain: string) => {
        this.registerLogo(domain);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Track a left-sidebar menu item click (all items share this one event). */
  protected trackNavItem(group: MenuNode, item: MenuNode): void {
    this.analytics.track(NAVIGATION_EVENTS.NAVIGATION_MENU_ITEM_SELECTED, {
      item: item.title,
      group: group.title,
      url: typeof item.url === 'string' ? item.url : '',
    });
  }

  /** Track the collapse/expand chevron. `expanded` still holds the pre-click
   * value here (the parent updates the input on the next change-detection
   * cycle), so the new collapsed state equals the current `expanded`. */
  protected trackSidenavToggle(): void {
    this.analytics.track(NAVIGATION_EVENTS.NAVIGATION_MENU_COLLAPSE_TOGGLED, {
      collapsed: this.expanded,
    });
  }

  /**
   * Register main logo icons: the collapsed mark and the full logo.
   */
  private registerLogo(domain: string): void {
    this.iconRegistry.addSvgIcon(
      'main-logo',
      this.assetUrl(domain, 'new-logo-short.svg')
    );
    this.iconRegistry.addSvgIcon(
      'main-logo-full',
      this.assetUrl(domain, 'new-logo-primary.svg')
    );
  }

  private assetUrl(domain: string, filename: string): SafeResourceUrl {
    const base = this.environment.production
      ? domain
      : `//${this.document.location.host}`;

    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `${base}/assets/img/${filename}`
    );
  }
}
